package com.siriusxm.unifiedcontent.console.ucp

import com.siriusxm.pia.ApplicationContext
import com.siriusxm.service.registry.ServiceRegistry
import com.siriusxm.service.registry.Team
import io.ktor.client.HttpClient
import io.ktor.client.plugins.contentnegotiation.ContentNegotiation
import io.ktor.serialization.kotlinx.json.json
import kotlinx.coroutines.CancellationException
import kotlinx.serialization.json.Json
import smithy4kt.SmithyClientError

/**
 * What reading a producer's or consumer's linked UCP team produced. Each outcome is its own case
 * because the Team section tells the user different things for each: a missing team is a data problem
 * to fix on the record, an unavailable UCP is transient and says nothing about the record.
 */
sealed interface UcpTeamResult {
    /** The team was read. [Team.members] is populated, and is empty for a team nobody has joined. */
    data class Found(val team: Team) : UcpTeamResult

    /** UCP has no team (or the viewer cannot see one) with the stored id. */
    data object NotFound : UcpTeamResult

    /** UCP could not be reached, rejected the token, or is not configured for this environment. */
    data class Unavailable(val reason: String) : UcpTeamResult
}

/**
 * Reads teams from Unified Content Permissions (the service registry API) with the viewer's token
 * (UCA-1212). The console only displays what UCP returns: it never writes a team or its membership.
 *
 * @param baseUrl the UCP API base URL from `ucpApiUrl` in config.json, or null when not configured
 */
class UcpTeamClient(private val context: ApplicationContext, private val baseUrl: String?) {

    private val api: ServiceRegistry? by lazy {
        val url = baseUrl?.takeIf { it.isNotBlank() } ?: return@lazy null
        ServiceRegistry(
            url,
            HttpClient {
                install(ContentNegotiation) {
                    json(Json {
                        isLenient = true
                        ignoreUnknownKeys = true
                    })
                }
                context.installAuth(this)
            }
        )
    }

    /** Read the team [teamId] (a team id or friendly id) with its members. */
    suspend fun team(teamId: String): UcpTeamResult {
        val client = api ?: return UcpTeamResult.Unavailable("UCP is not configured for this environment.")
        return try {
            UcpTeamResult.Found(client.getTeam(teamId, includeMembers = true))
        } catch (e: CancellationException) {
            throw e
        } catch (e: SmithyClientError) {
            if (e.httpStatusCode == 404) UcpTeamResult.NotFound
            else UcpTeamResult.Unavailable(e.message ?: "UCP rejected the request (${e.httpStatusCode}).")
        } catch (e: Throwable) {
            UcpTeamResult.Unavailable(e.message ?: "UCP could not be reached.")
        }
    }
}
